// AppCombobox — a SEARCHABLE single choice for a LARGE option set (a contact
// among 301, a product among thousands). DESIGN_SPEC §5.4, L65: a native
// <select> with hundreds of rows is not acceptable UX, and the built-in
// `SearchDropdown` (system/default) fails the combobox pattern — measured
// from its source 2026-09-26: its trigger is a click-only <div> with no
// tabindex (the keyboard never reaches it), no combobox / listbox / option
// roles, no aria-expanded / aria-activedescendant, and hard-coded English.
//
//   AppCombobox: {
//     ariaLabel: (el) => el.call('polyglot', 'crm.deal.contact'),
//     placeholder: 'crm.deal.contactPlaceholder',   // a bundle key or a string
//     options: (el, s) => s.root.crmContacts.map((c) => ({
//       value: c.id, label: c.name, hint: c.email     // hint: optional
//     })),
//     current: (el, s) => s.root.crmDealDraft?.contactId,
//     select: 'crmDealSetContact'                   // el.call(select, value)
//   }
//
// The WAI-ARIA combobox (1.2, list autocomplete): the input is the combobox —
// it keeps focus the whole time; ↓ / ↑ move the active option (announced by
// aria-activedescendant), Enter picks it, Esc closes (a second Esc leaves the
// field alone), Tab closes without picking. Typing filters by label and hint,
// case- and accent-insensitively; the list shows the first `limit` matches
// and says how many more there are.
//
// THE LIST OPENS IN FLOW, under the field — a disclosure, not a floating
// popover — so no scroll container can clip it: a combobox mostly lives in a
// dialog body, which IS a scroll container (the floating form waits on the
// framework popover host, IMPLEMENTATION_PLAN §8 F2). Closed, it takes no
// box. Rows are the house list row (radiusControl, the `hover` wash, the
// `selected` wash on the active row, the check on the current value).
//
// Its own UI state (query / open / active) is LOCAL; the chosen value lives
// wherever `current` reads it and changes only through `select`.
export const AppCombobox = {
  options: [],
  current: null,
  select: '',
  placeholder: '',
  limit: 50,

  // The combobox's private helpers travel WITH it (frank-safe: `scope`, not
  // module-level functions); every descendant reaches them as `el.scope.X`.
  scope: {
    norm(v) {
      return String(v || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
    },
    id(box, part) {
      const path = (typeof box.getPath === 'function' && box.getPath()) || [box.key]
      return 'cbx-' + path.join('-').replace(/[^\w-]/g, '') + '-' + part
    },
    matches(box, s) {
      const all = (Array.isArray(box.options) ? box.options : []).filter(Boolean)
      const q = this.norm(s && s.query)
      const hits = q
        ? all.filter((o) => this.norm(o.label).includes(q) || this.norm(o.hint).includes(q))
        : all
      const shown = hits.slice(0, box.limit || 50)
      const more = hits.length - shown.length
      const statusKey = !hits.length
        ? 'common.state.noMatches'
        : more > 0
          ? 'common.hint.keepTyping'
          : ''
      return { shown, more, statusKey }
    },
    active(box, s) {
      const n = this.matches(box, s).shown.length
      return n ? Math.max(0, Math.min((s && s.active) || 0, n - 1)) : -1
    },
    label(box) {
      const c = box.current
      const hit = (Array.isArray(box.options) ? box.options : []).find((o) => o && o.value === c)
      return hit ? String(hit.label) : ''
    },
    open(box) {
      const s = box.state
      const shown = this.matches(box, { query: '' }).shown
      const at = shown.findIndex((o) => o.value === box.current)
      s.update({ open: true, query: '', active: at === -1 ? 0 : at })
    },
    pick(box, value) {
      if (typeof box.select === 'string' && box.select) box.call(box.select, value)
      box.state.update({ open: false, query: '' })
    },
    reveal(box, index) {
      const list = box.ComboList && box.ComboList.node
      const opt = box.ComboList && box.ComboList.getChildren()[index]
      if (!list || !opt || !opt.node) return
      const top = opt.node.offsetTop
      const bottom = top + opt.node.offsetHeight
      if (top < list.scrollTop) list.scrollTo({ top })
      else if (bottom > list.scrollTop + list.clientHeight)
        list.scrollTo({ top: bottom - list.clientHeight })
    },
    key(box, e) {
      const s = box.state
      const shown = this.matches(box, s).shown
      const n = shown.length
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault()
        if (!s.open) return this.open(box)
        if (!n) return
        const next = (this.active(box, s) + (e.key === 'ArrowDown' ? 1 : -1) + n) % n
        s.update({ active: next })
        this.reveal(box, next)
      } else if (e.key === 'Enter') {
        if (!s.open || !n) return
        e.preventDefault()
        this.pick(box, shown[this.active(box, s)].value)
      } else if (e.key === 'Escape') {
        // The first Esc closes the list and stops there; with the list closed
        // it bubbles on (an enclosing dialog closes).
        if (!s.open) return
        e.preventDefault()
        e.stopPropagation()
        s.update({ open: false, query: '' })
      } else if (e.key === 'Tab') {
        if (s.open) s.update({ open: false, query: '' })
      }
    }
  },

  state: { query: '', open: false, active: 0 },
  flow: 'y',
  gap: '0',
  width: '100%',
  minWidth: '0',

  // The field: the kit's input group (one surface, one edge, one ring).
  // `ComboList` / `ComboStatus` are distinctive on purpose — `Field`,
  // `Search`, `Group` are system/default components, and a bare key
  // auto-extends its namesake.
  AppInputGroup: {
    gap: 'Y',

    AppInputBare: {
      type: 'text',
      role: 'combobox',
      autocomplete: 'off',
      spellcheck: 'false',
      ariaAutocomplete: 'list',
      ariaExpanded: (el, s) => (s.open ? 'true' : 'false'),
      ariaControls: (el) => el.scope.id(el.parent.parent, 'list'),
      ariaActivedescendant: (el, s) => {
        const at = s.open ? el.scope.active(el.parent.parent, s) : -1
        return at === -1 ? null : el.scope.id(el.parent.parent, 'opt-' + at)
      },
      // the accessible name: the AppField label around the combobox, else
      // its own `ariaLabel`
      ariaLabel: (el) => {
        const box = el.parent.parent
        const field = box.parent
        const own = field && field.label
        if (own) return typeof own === 'function' ? own(field, field.state) : own
        const v = box.ariaLabel
        return (typeof v === 'function' ? v(box, box.state) : v) || null
      },
      placeholder: (el) => {
        const box = el.parent.parent
        const v =
          typeof box.placeholder === 'function' ? box.placeholder(box, box.state) : box.placeholder
        return v ? el.call('polyglot', v) : null
      },
      // Open: what is being typed. Closed: the chosen option's label.
      value: (el, s) => (s.open ? s.query : el.scope.label(el.parent.parent)),
      onFocus: (e, el) => el.scope.open(el.parent.parent),
      onInput: (e, el, s) => s.update({ query: e.target.value, open: true, active: 0 }),
      onBlur: (e, el, s) => s.update({ open: false, query: '' }),
      onKeydown: (e, el) => el.scope.key(el.parent.parent, e)
    },

    // the disclosure glyph — decoration; the field itself is the control
    Icon: {
      name: 'chevronDown',
      flexShrink: '0',
      color: 'caption',
      pointerEvents: 'none',
      ariaHidden: 'true',
      transition: 'transform A defaultBezier',
      transform: (el, s) => (s.open ? 'rotate(180deg)' : 'none')
    }
  },

  ComboList: {
    role: 'listbox',
    id: (el) => el.scope.id(el.parent, 'list'),
    hide: (el, s) => !s.open,
    flow: 'y',
    gap: '0',
    marginTop: 'X',
    maxHeight: 'G',
    overflow: 'auto',
    overscrollBehavior: 'contain',
    padding: 'Y',
    round: 'radiusCard',
    theme: 'layer',
    // A press inside the list must not blur the input first (that would
    // close the list before the click lands).
    onMousedown: (e) => e.preventDefault(),
    childrenAs: 'state',
    children: (el, s) => {
      // A closed control keeps its listbox target, but no hidden option tree.
      // Query once for the list. Each row uses its snapshot's count for the
      // active highlight instead of scanning the full option set again.
      if (!s.open) return []
      const shown = el.scope.matches(el.parent, s).shown
      return shown.map((o, i) => ({
        key: 'o-' + o.value,
        value: o.value,
        label: String(o.label),
        hint: o.hint ? String(o.hint) : '',
        index: i,
        optionCount: shown.length
      }))
    },
    childProps: {
      tag: 'div',
      role: 'option',
      id: (el, s) => el.scope.id(el.parent.parent, 'opt-' + s.index),
      ariaSelected: (el, s) => (el.parent.parent.current === s.value ? 'true' : 'false'),
      // flat hook: FW-ELEMENT-DEFINITION-DATA-REPLACES-EXTENDS-DATA-1
      dataActive: (el, s) =>
        s.index === Math.max(0, Math.min(el.parent.state.active || 0, s.optionCount - 1)),
      display: 'flex',
      flow: 'x',
      align: 'center',
      gap: 'Z',
      padding: 'Y Z1',
      round: 'radiusControl',
      cursor: 'pointer',
      color: 'title',
      fontSize: 'Z1',
      ':hover': { background: 'hover' },
      ':active': { background: 'selected' },
      '&[data-active]': { background: 'selected' },
      onClick: (e, el, s) => el.scope.pick(el.parent.parent, s.value),
      // THE TEXT LAW (overflow law §3b.8): the option's primary text WRAPS,
      // never an ellipsis; the hint is secondary meta — it may ellipsize, and
      // then carries its full text in `title`.
      OptionLabel: {
        tag: 'span',
        flex: '1 1 auto',
        minWidth: '0',
        whiteSpace: 'normal',
        overflowWrap: 'anywhere',
        text: (el, s) => s.label
      },
      OptionHint: {
        tag: 'span',
        flexShrink: '1',
        minWidth: '0',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        fontSize: 'Z',
        color: 'caption',
        hide: (el, s) => !s.hint,
        title: (el, s) => s.hint || null,
        text: (el, s) => s.hint
      },
      Icon: {
        name: 'checkmark',
        flexShrink: '0',
        ariaHidden: 'true',
        visibility: (el, s) => (el.parent.parent.parent.current === s.value ? 'visible' : 'hidden')
      }
    }
  },

  // "No matches" / "12 more — keep typing": one quiet line under the rows.
  // A live region is announced only if it EXISTS before its text changes, so
  // it stays mounted (never hidden) and is simply empty — no box — when there
  // is nothing to say.
  ComboStatus: {
    tag: 'span',
    role: 'status',
    padding: '0 Z1',
    fontSize: 'Z',
    color: 'caption',
    marginTop: (el, s) => (s.open && el.scope.matches(el.parent, s).statusKey ? 'X' : '0'),
    text: (el, s) => {
      const m = s.open ? el.scope.matches(el.parent, s) : null
      if (!m || !m.statusKey) return ''
      return el.call('polyglot', m.statusKey).replace('{count}', String(m.more))
    }
  }
}
